<template>
    <div>
        <div class="container-fluid substitutes-tree-page">
            <div class="card tree-card mt-4">
                <div class="card-body">
                    <div
                        class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                    >
                        <h5 class="mb-0">Substitute Tree</h5>
                        <form class="oneline-form" @submit.prevent="loadTree">
                            <div class="input-group input-group-md">
                                <input
                                    name="part_number"
                                    v-model="partInput"
                                    type="text"
                                    class="form-control"
                                    placeholder="Enter part number"
                                    :disabled="isFetching"
                                />
                                <button class="btn btn-danger" type="submit" :disabled="isFetching">
                                    <span
                                        v-if="isFetching"
                                        class="spinner-border spinner-border-sm"
                                    ></span>
                                    <span v-else>Load Tree</span>
                                </button>
                                <button
                                    class="btn btn-outline-secondary"
                                    type="button"
                                    @click="resetInput"
                                    :disabled="isFetching && !partInput"
                                >
                                    Clear
                                </button>
                            </div>
                        </form>
                        <span v-if="activePart" class="badge bg-light text-dark" v-show="false">
                            Active part: <strong>{{ activePart }}</strong>
                        </span>
                    </div>
                    <SubstitutesTreeView
                        :root-part="activePart"
                        :part-label="activePart"
                        :show-select-button="false"
                        :enable-remove-request="true"
                        @remove-request="onRemoveRequest"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import SubstitutesTreeView from '@/components/substitutes/SubstitutesTreeView.vue'
    import { normalizePart } from '@/helpers/string.js'
    import { mapStores } from 'pinia'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    export default {
        name: 'SubstitutesTreePage',
        components: { SubstitutesTreeView },
        data() {
            return {
                partInput: '',
                activePart: '',
                isFetching: false,
                displayQuoteButton: false,
                is_bpa_noquote: false,
            }
        },
        computed: {
            ...mapStores(useSubstitutesStore),
        },
        created() {
            const saved = this.substitutesStore.treePageState || {}
            if (typeof saved.partInput === 'string') {
                this.partInput = saved.partInput
            }
            if (typeof saved.activePart === 'string') {
                this.activePart = saved.activePart
            }
        },
        watch: {
            partInput() {
                this.persistTreePageState()
            },
            activePart() {
                this.persistTreePageState()
            },
        },
        methods: {
            async loadTree() {
                const normalized = normalizePart(this.partInput)
                if (!normalized) return

                this.activePart = ''
                this.isFetching = true
                try {
                    await this.substitutesStore.fetchSubstitutes([normalized])
                    await this.substitutesStore.fetchSubstituteStats({
                        parts: [normalized],
                        statsData: this.searchStore.search?.statsData,
                        vuexStore: this.searchStore,
                    })
                    this.activePart = normalized
                    this.partInput = normalized
                } catch (error) {
                    console.error('Failed to load substitutes tree', error)
                } finally {
                    this.isFetching = false
                }
            },
            resetInput() {
                this.partInput = ''
                this.activePart = ''
            },
            onRemoveRequest(payload) {
                this.$emit('select-unlink-substitute', payload)
            },
            persistTreePageState() {
                this.substitutesStore.setTreePageState({
                    partInput: this.partInput,
                    activePart: this.activePart,
                })
            },
        },
    }
</script>

<style scoped lang="scss">
    .tree-card {
        border: 1px solid var(--color-border-default);
        border-top: 4px solid var(--color-brand-primary);
        border-radius: var(--radius-md);
        background: var(--color-surface-canvas);
        box-shadow: var(--shadow-sm);
    }

    .tree-card :deep(.card-body) {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .tree-card h5 {
        color: var(--color-text-primary);
    }

    .input-group {
        max-width: 600px;
    }

    .input-group :deep(.form-control) {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        border-color: var(--color-border-default);
    }

    .input-group :deep(.form-control::placeholder) {
        color: var(--color-text-muted);
    }

    .input-group :deep(.form-control:focus) {
        border-color: var(--color-border-accent-strong);
        box-shadow: var(--shadow-focus-brand);
    }

    .input-group :deep(.btn-outline-secondary) {
        background: var(--color-surface-canvas);
        color: var(--color-text-secondary);
        border-color: var(--color-border-default);
    }

    .input-group :deep(.btn-outline-secondary:hover) {
        background: var(--color-surface-subtle);
        color: var(--color-text-primary);
        border-color: var(--color-border-default);
    }

    .substitutes-tree-page {
        padding: 2rem;
    }
    .oneline-form {
        height: auto;
    }
    .search-card {
        padding: 0;
    }

    @media (max-width: 768px) {
        .substitutes-tree-page {
            padding: 1rem;
        }
    }
</style>
